"use client";

import {NeoTextBox} from "@/components/forms";
import {SearchIcon} from "@/components/shared";
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from "@nextui-org/react";
import {useEffect, useState} from "react";
import {FaArrowDownLong, FaArrowUpLong} from "react-icons/fa6";

export function ViewTable({
  tabledatas,
  onSelectionRow
}: {
  tabledatas: any;
  onSelectionRow: (selectedRows: any[]) => void;
}) {
  const [tableData, setTableData] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  if (tabledatas.length === 0) {
    tabledatas = {
      tableheaders: [],
      tablerows: [],
      checkboxcheck: false
    };
  }

  useEffect(() => {
    setSelectedKeys(new Set());
    if (tabledatas?.tablerows) {
      setTableData(tabledatas.tablerows);
    }
  }, [tabledatas]);

  let filteredRows = Array.isArray(tabledatas?.tablerows)
    ? tabledatas.tablerows
    : [];
  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filteredRows = filteredRows.filter((row: any) =>
      Object.values(row).some(val => val?.toString().toLowerCase().includes(q))
    );
  }

  let sortedRows = [...filteredRows];
  if (sortKey) {
    sortedRows.sort((a, b) => {
      const valA = (a[sortKey] ?? "").toString().toLowerCase();
      const valB = (b[sortKey] ?? "").toString().toLowerCase();
      if (valA < valB) return sortOrder === "asc" ? -1 : 1;
      if (valA > valB) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }

  const totalRows = sortedRows.length;
  const totalPages = Math.ceil(totalRows / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentRows = sortedRows.slice(startIndex, endIndex);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const onSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const onRowsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  };

  useEffect(() => {
    if (Array.isArray(tableData)) {
      const selected = tableData.filter((row: any) =>
        selectedKeys.has(row.userId)
      );
      onSelectionRow(selected);
    }
  }, [selectedKeys, tableData]);

  const handleSelectionChange = (keys: any) => {
    const allRowIds = tableData.map(row => row.userId);
    if (keys === "all") {
      setSelectedKeys(new Set(allRowIds));
    } else {
      setSelectedKeys(new Set(keys));
    }
  };

  return (
    <div className="flex w-full max-w-screen-xl flex-col gap-2">
      <div className="flex justify-between">
        <NeoTextBox
          isClearable
          className="sm:max-w-[40%]"
          placeholder="Search..."
          startContent={<SearchIcon />}
          size="sm"
          onValueChange={onSearchChange}
        />
        <div className="mb-2 flex items-center justify-end">
          <label className="flex items-center text-small text-default-400">
            Rows per page:
            <select
              className="ml-2 bg-transparent text-small text-default-400 outline-none"
              onChange={onRowsPerPageChange}
              value={rowsPerPage}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
              <option value="100">100</option>
            </select>
          </label>
        </div>
      </div>

      <Table
        aria-label="Student Details"
        className="border-none shadow-none"
        selectionMode={tabledatas.checkboxcheck ? "multiple" : undefined}
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        removeWrapper
      >
        <TableHeader>
          {tabledatas.tableheaders.map((header: any) => (
            <TableColumn
              key={header.key}
              onClick={() => handleSort(header.key)}
              className="cursor-pointer"
            >
              <span className="inline-flex items-center gap-1">
                {header.label}
                {sortKey === header.key &&
                  (sortOrder === "asc" ? (
                    <FaArrowUpLong className="text-xs" />
                  ) : (
                    <FaArrowDownLong className="text-xs" />
                  ))}
              </span>
            </TableColumn>
          ))}
        </TableHeader>

        <TableBody items={currentRows} emptyContent="No data available">
          {(row: any) => (
            <TableRow key={row.userId}>
              {tabledatas.tableheaders.map((headerkey: any) => (
                <TableCell key={headerkey.key}>
                  {row[headerkey.key] ?? "N/A"}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>

      {totalPages > 0 && (
        <div className="flex items-center justify-between px-2 py-2">
          <Pagination
            showControls
            classNames={{cursor: "bg-foreground text-background"}}
            color="default"
            page={page}
            total={totalPages}
            variant="light"
            onChange={setPage}
          />
        </div>
      )}
    </div>
  );
}
